import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, User, Phone, Mail, Scissors, Check, ChevronLeft, ChevronRight, MapPin, Sparkles, Shield, Loader2 } from 'lucide-react';
import {
    publicBookingApi,
} from '../../api/publicBookingApi';
import type {
    PublicSalonResponse,
    PublicServiceResponse,
    PublicEmployeeResponse,
    TimeSlot,
    PublicBookingResponse,
    PublicBookingConfig
} from '../../api/publicBookingApi';
import './PublicBookingPage.css';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

type BookingStep = 'service' | 'employee' | 'datetime' | 'details' | 'verification' | 'confirmation';

export default function PublicBookingPage() {
    const { salonSlug, employeeId: paramEmployeeId } = useParams<{ salonSlug: string, employeeId?: string }>();
    const [searchParams] = useSearchParams();
    const isEmbedMode = searchParams.get('embed') === 'true';

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState<BookingStep>('service');

    // Salon data
    const [salon, setSalon] = useState<PublicSalonResponse | null>(null);
    const [services, setServices] = useState<PublicServiceResponse[]>([]);
    const [employees, setEmployees] = useState<PublicEmployeeResponse[]>([]);
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [bookingConfig, setBookingConfig] = useState<PublicBookingConfig | null>(null);

    // Selected values
    const [selectedService, setSelectedService] = useState<PublicServiceResponse | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<PublicEmployeeResponse | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [selectedTime, setSelectedTime] = useState<string | null>(null);

    // Client info
    const [clientName, setClientName] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [notes, setNotes] = useState('');

    // Phone verification
    const [verificationToken, setVerificationToken] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);
    const [verificationSending, setVerificationSending] = useState(false);
    const [verificationVerifying, setVerificationVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const [verificationSent, setVerificationSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Booking result
    const [bookingResult, setBookingResult] = useState<PublicBookingResponse | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // VIP Mode
    const [vipCode, setVipCode] = useState('');
    const [isVipVerified, setIsVipVerified] = useState(false);
    const [vipSubmitting, setVipSubmitting] = useState(false);
    const [vipError, setVipError] = useState<string | null>(null);

    const handleVipSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!salonSlug || !vipCode) return;
        try {
            setVipSubmitting(true);
            setVipError(null);
            const res = await publicBookingApi.verifyVipCode(salonSlug, vipCode);
            if (res.valid) {
                setIsVipVerified(true);
            } else {
                setVipError(res.message || 'Código inválido');
            }
        } catch (err: any) {
            setVipError(err.response?.data?.message || 'Código inválido');
        } finally {
            setVipSubmitting(false);
        }
    };

    // Load salon data
    useEffect(() => {
        if (!salonSlug) return;

        const loadSalon = async () => {
            try {
                setLoading(true);
                const [salonData, servicesData] = await Promise.all([
                    publicBookingApi.getSalon(salonSlug),
                    publicBookingApi.getServices(salonSlug)
                ]);
                setSalon(salonData);
                setServices(servicesData);
                setError(null);

                // Load booking config (non-blocking)
                try {
                    const config = await publicBookingApi.getBookingConfig(salonSlug);
                    setBookingConfig(config);
                } catch {
                    // If config endpoint doesn't exist yet, use defaults
                    setBookingConfig({
                        requirePhoneVerification: false,
                        bookingMode: 'OPEN',
                        showPricing: true,
                        showEmployeeFullNames: true,
                        bookingWindowDays: 30,
                        vipModeEnabled: false
                    });
                }
            } catch (err) {
                console.error('Error loading salon:', err);
                setError('No pudimos encontrar este salón. Verifica el enlace e intenta de nuevo.');
            } finally {
                setLoading(false);
            }
        };

        loadSalon();
    }, [salonSlug]);

    // Load employees (either all or pinned context)
    useEffect(() => {
        if (!salonSlug) return;

        const loadEmployees = async () => {
            try {
                // If service is selected, fetch compatible employees. 
                // If not, but we have a pinned employee, fetch all to find the pinned one.
                if (selectedService || paramEmployeeId) {
                    const employeesData = await publicBookingApi.getEmployees(salonSlug, selectedService?.id);
                    setEmployees(employeesData);

                    if (paramEmployeeId && !selectedEmployee) {
                        const pinned = employeesData.find(e => e.id === paramEmployeeId);
                        if (pinned) {
                            setSelectedEmployee(pinned);
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading employees:', err);
            }
        };

        loadEmployees();
    }, [salonSlug, selectedService, paramEmployeeId]);

    // Load availability when date/employee changes
    useEffect(() => {
        if (!salonSlug || !selectedService || !selectedEmployee) return;

        const loadAvailability = async () => {
            try {
                const dateStr = selectedDate.toISOString().split('T')[0];
                const availability = await publicBookingApi.getAvailability(
                    salonSlug,
                    dateStr,
                    selectedService.id,
                    selectedEmployee.id
                );
                setTimeSlots(availability.availableSlots);
            } catch (err) {
                console.error('Error loading availability:', err);
            }
        };

        loadAvailability();
    }, [salonSlug, selectedService, selectedEmployee, selectedDate]);

    // Cooldown timer for resending verification
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Build the steps based on whether verification is needed
    const getSteps = (): BookingStep[] => {
        const needsVerification = bookingConfig?.requirePhoneVerification && !verificationToken;
        const steps: BookingStep[] = ['service'];
        if (!paramEmployeeId) {
            steps.push('employee');
        }
        steps.push('datetime', 'details');
        if (needsVerification) {
            steps.push('verification');
        }
        steps.push('confirmation');
        return steps;
    };

    // Navigation functions
    const goNext = () => {
        const steps = getSteps();
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex < steps.length - 1) {
            setCurrentStep(steps[currentIndex + 1]);
        }
    };

    const goBack = () => {
        const steps = getSteps();
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex > 0) {
            setCurrentStep(steps[currentIndex - 1]);
        }
    };

    // Date navigation
    const changeDate = (days: number) => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + days);
        // Don't allow past dates
        if (newDate >= new Date(new Date().setHours(0, 0, 0, 0))) {
            setSelectedDate(newDate);
            setSelectedTime(null);
        }
    };

    // Format helpers
    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const formatDuration = (minutes: number) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        };
        return date.toLocaleDateString('es-CO', options);
    };

    // Phone verification logic
    const sendVerificationCode = async () => {
        if (!salonSlug || !clientPhone) return;
        try {
            setVerificationSending(true);
            setVerificationError(null);
            await publicBookingApi.sendVerificationCode(salonSlug, clientPhone);
            setVerificationSent(true);
            setCooldown(60);
        } catch (err: unknown) {
            console.error('Error sending verification:', err);
            const data = (err as any)?.response?.data;
            const msg = data?.message || data?.error || 'Error al enviar código de verificación. Intenta de nuevo.';
            setVerificationError(msg);
        } finally {
            setVerificationSending(false);
        }
    };

    const handleCodeChange = (index: number, value: string) => {
        if (value.length > 1) value = value.slice(-1);
        if (!/^\d*$/.test(value)) return;

        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all digits entered
        if (newCode.every(c => c !== '')) {
            confirmCode(newCode.join(''));
        }
    };

    const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    const confirmCode = async (code: string) => {
        if (!salonSlug) return;
        try {
            setVerificationVerifying(true);
            setVerificationError(null);
            const result = await publicBookingApi.confirmVerificationCode(salonSlug, clientPhone, code);
            if (result.verified && result.token) {
                setVerificationToken(result.token);
                // Auto-proceed to booking submission
                submitBookingWithToken(result.token);
            } else {
                setVerificationError(result.message || 'Código inválido');
                setVerificationCode(['', '', '', '', '', '']);
                codeInputRefs.current[0]?.focus();
            }
        } catch (err: unknown) {
            console.error('Error confirming code:', err);
            const data = (err as any)?.response?.data;
            const msg = data?.message || data?.error || 'Código inválido o expirado.';
            setVerificationError(msg);
            setVerificationCode(['', '', '', '', '', '']);
            codeInputRefs.current[0]?.focus();
        } finally {
            setVerificationVerifying(false);
        }
    };

    // Submit booking (called from details step if no verification, or after verification)
    const submitBooking = async () => {
        if (bookingConfig?.requirePhoneVerification && !verificationToken) {
            // Need verification first — go to verification step
            goNext(); // Goes to verification step
            // Auto-send code
            if (!verificationSent) {
                setTimeout(() => sendVerificationCode(), 300);
            }
            return;
        }

        // If we already have token or verification isn't required
        await submitBookingWithToken(verificationToken);
    };

    const submitBookingWithToken = async (token: string | null) => {
        if (!salonSlug || !selectedService?.id || !selectedEmployee?.id || !selectedTime) {
            console.error('Validation failed: Missing required booking data', { 
                salonSlug, 
                serviceId: selectedService?.id, 
                employeeId: selectedEmployee?.id, 
                selectedTime 
            });
            setError('Faltan datos obligatorios para completar la reserva. Por favor, selecciona un servicio y profesional.');
            return;
        }

        try {
            setSubmitting(true);

            // Build datetime
            const [hours, minutes] = selectedTime.split(':').map(Number);
            const startTime = new Date(selectedDate);
            startTime.setHours(hours, minutes, 0, 0);

            const result = await publicBookingApi.createBooking(salonSlug, {
                employeeId: selectedEmployee.id,
                serviceId: selectedService.id,
                clientName,
                clientPhone,
                clientEmail: clientEmail || undefined,
                startTime: startTime.toISOString(),
                notes: notes || undefined,
                verificationToken: token || undefined,
                recaptchaToken: await (async () => {
                    try {
                        const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
                        if (!siteKey || !window.grecaptcha?.enterprise) return undefined;
                        
                        return await window.grecaptcha.enterprise.execute(siteKey, { action: 'BOOKING' });
                    } catch (err) {
                        console.error('reCAPTCHA execution failed:', err);
                        return undefined;
                    }
                })(),
                vipCode: bookingConfig?.vipModeEnabled ? vipCode : undefined,
            });

            setBookingResult(result);
            setCurrentStep('confirmation');
        } catch (err) {
            console.error('Error creating booking:', err);
            setError('No pudimos crear tu reserva. Por favor intenta de nuevo.');
        } finally {
            setSubmitting(false);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className="public-booking-page dark">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    // Render error state
    if (error && !salon) {
        return (
            <div className="public-booking-page dark">
                <div className="error-container">
                    <div className="error-icon">😕</div>
                    <h2>Salón no encontrado</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    // Render step indicator
    const renderStepIndicator = () => {
        const stepsConfig = [
            { key: 'service', label: 'Servicio', icon: Scissors },
        ];

        if (!paramEmployeeId) {
            stepsConfig.push({ key: 'employee', label: 'Profesional', icon: User });
        }

        stepsConfig.push(
            { key: 'datetime', label: 'Fecha y Hora', icon: Calendar },
            { key: 'details', label: 'Tus Datos', icon: Phone }
        );

        // Add verification step if needed
        if (bookingConfig?.requirePhoneVerification && !verificationToken) {
            stepsConfig.push({ key: 'verification', label: 'Verificar', icon: Shield });
        }

        return (
            <div className="step-indicator">
                {stepsConfig.map((step, index) => (
                    <div
                        key={step.key}
                        className={`step ${currentStep === step.key ? 'active' : ''} ${stepsConfig.findIndex(s => s.key === currentStep) > index ? 'completed' : ''
                            }`}
                    >
                        <div className="step-circle">
                            {stepsConfig.findIndex(s => s.key === currentStep) > index ? (
                                <Check className="check-icon" />
                            ) : (
                                <step.icon className="step-icon" />
                            )}
                        </div>
                        <span className="step-label">{step.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    // Render service selection
    const renderServiceStep = () => (
        <div className="step-content">
            <h2 className="step-title">¿Qué servicio te gustaría?</h2>
            {paramEmployeeId && selectedEmployee && (
                <div className="pinned-employee-badge">
                    <User className="icon-small" /> Reservando con {selectedEmployee.fullName}
                </div>
            )}
            <div className="services-grid">
                {services.map(service => (
                    <div
                        key={service.id}
                        className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}
                        onClick={() => {
                            setSelectedService(service);
                            if (!paramEmployeeId) {
                                setSelectedEmployee(null);
                            }
                            setSelectedTime(null);
                        }}
                    >
                        <div className="service-header">
                            <h3>{service.name}</h3>
                            {(bookingConfig?.showPricing !== false) && (
                                <span className="service-price">{formatPrice(service.price)}</span>
                            )}
                        </div>
                        <div className="service-details">
                            <span className="service-duration">
                                <Clock className="icon-small" />
                                {formatDuration(service.durationMinutes)}
                            </span>
                        </div>
                        {selectedService?.id === service.id && (
                            <div className="selected-indicator">
                                <Check />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="step-actions">
                <button
                    className="btn-primary"
                    onClick={goNext}
                    disabled={!selectedService}
                >
                    Continuar
                </button>
            </div>
        </div>
    );

    // Render employee selection
    const renderEmployeeStep = () => (
        <div className="step-content">
            <h2 className="step-title">¿Con quién prefieres tu cita?</h2>
            <div className="employees-grid">
                {employees.map(employee => (
                    <div
                        key={employee.id}
                        className={`employee-card ${selectedEmployee?.id === employee.id ? 'selected' : ''}`}
                        onClick={() => {
                            setSelectedEmployee(employee);
                            setSelectedTime(null);
                        }}
                    >
                        <div className="employee-avatar">
                            {employee.photoUrl ? (
                                <img src={employee.photoUrl} alt={employee.fullName} />
                            ) : (
                                <span>{employee.fullName.charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <h3>{employee.fullName}</h3>
                        <span className="employee-role">{employee.role}</span>
                        {selectedEmployee?.id === employee.id && (
                            <div className="selected-indicator">
                                <Check />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="step-actions">
                <button className="btn-secondary" onClick={goBack}>
                    <ChevronLeft /> Atrás
                </button>
                <button
                    className="btn-primary"
                    onClick={goNext}
                    disabled={!selectedEmployee}
                >
                    Continuar
                </button>
            </div>
        </div>
    );

    // Render date/time selection
    const renderDateTimeStep = () => (
        <div className="step-content">
            <h2 className="step-title">Elige fecha y hora</h2>

            <div className="date-selector">
                <button
                    className="date-nav-btn"
                    onClick={() => changeDate(-1)}
                    disabled={selectedDate <= new Date(new Date().setHours(0, 0, 0, 0))}
                >
                    <ChevronLeft />
                </button>
                <div className="date-display">
                    <Calendar className="calendar-icon" />
                    <span>{formatDate(selectedDate)}</span>
                </div>
                <button className="date-nav-btn" onClick={() => changeDate(1)}>
                    <ChevronRight />
                </button>
            </div>

            <div className="time-slots-container">
                <h3>Horarios disponibles</h3>
                <div className="time-slots-grid">
                    {timeSlots.length === 0 ? (
                        <p className="no-slots">Cargando horarios...</p>
                    ) : timeSlots.filter(slot => slot.available).length === 0 ? (
                        <p className="no-slots">No hay horarios disponibles para esta fecha</p>
                    ) : (
                        timeSlots.filter(slot => slot.available).map(slot => (
                            <button
                                key={slot.time}
                                className={`time-slot ${selectedTime === slot.time ? 'selected' : ''}`}
                                onClick={() => setSelectedTime(slot.time)}
                            >
                                {slot.time}
                            </button>
                        ))
                    )}
                </div>
            </div>

            <div className="step-actions">
                <button className="btn-secondary" onClick={goBack}>
                    <ChevronLeft /> Atrás
                </button>
                <button
                    className="btn-primary"
                    onClick={goNext}
                    disabled={!selectedTime}
                >
                    Continuar
                </button>
            </div>
        </div>
    );

    // Render client details form
    const renderDetailsStep = () => (
        <div className="step-content">
            <h2 className="step-title">Tus datos de contacto</h2>

            <div className="booking-summary">
                <h3>Resumen de tu reserva</h3>
                <div className="summary-item">
                    <Scissors />
                    <span>{selectedService?.name} - {formatPrice(selectedService?.price || 0)}</span>
                </div>
                <div className="summary-item">
                    <User />
                    <span>con {selectedEmployee?.fullName}</span>
                </div>
                <div className="summary-item">
                    <Calendar />
                    <span>{formatDate(selectedDate)} a las {selectedTime}</span>
                </div>
            </div>

            {/* Verification badge if phone is already verified */}
            {verificationToken && (
                <div className="verified-badge">
                    <Shield className="verified-icon" />
                    <span>Teléfono verificado ✓</span>
                </div>
            )}

            <form className="client-form" onSubmit={(e) => { e.preventDefault(); submitBooking(); }}>
                <div className="form-group">
                    <label htmlFor="clientName">
                        <User className="form-icon" />
                        Nombre completo *
                    </label>
                    <input
                        type="text"
                        id="clientName"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Tu nombre"
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="clientPhone">
                        <Phone className="form-icon" />
                        Teléfono (WhatsApp) *
                        {bookingConfig?.requirePhoneVerification && (
                            <span className="verification-required-label">Se verificará</span>
                        )}
                    </label>
                    <input
                        type="tel"
                        id="clientPhone"
                        value={clientPhone}
                        onChange={(e) => {
                            setClientPhone(e.target.value);
                            // Reset verification if phone changes
                            if (verificationToken) {
                                setVerificationToken(null);
                                setVerificationSent(false);
                                setVerificationCode(['', '', '', '', '', '']);
                            }
                        }}
                        placeholder="300 123 4567"
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="clientEmail">
                        <Mail className="form-icon" />
                        Email (opcional)
                    </label>
                    <input
                        type="email"
                        id="clientEmail"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="tu@email.com"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="notes">
                        Notas adicionales (opcional)
                    </label>
                    <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="¿Algo que debamos saber?"
                        rows={3}
                    />
                </div>

                {error && (
                    <div className="form-error">
                        <p>{error}</p>
                    </div>
                )}

                <div className="step-actions">
                    <button type="button" className="btn-secondary" onClick={goBack}>
                        <ChevronLeft /> Atrás
                    </button>
                    <button
                        type="submit"
                        className="btn-primary btn-confirm"
                        disabled={!clientName || !clientPhone || !selectedService?.id || !selectedEmployee?.id || submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="icon-spin" /> Reservando...
                            </>
                        ) : bookingConfig?.requirePhoneVerification && !verificationToken ? (
                            <>
                                <Shield /> Verificar y Reservar
                            </>
                        ) : (
                            '✨ Confirmar Reserva'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    // Render phone verification step
    const renderVerificationStep = () => (
        <div className="step-content verification-step">
            <div className="verification-header">
                <div className="verification-shield">
                    <Shield />
                </div>
                <h2 className="step-title">Verifica tu teléfono</h2>
                <p className="verification-subtitle">
                    Por seguridad, necesitamos verificar tu número de teléfono
                    <strong> {clientPhone}</strong>
                </p>
            </div>

            {!verificationSent ? (
                <div className="verification-send-section">
                    <p className="verification-info">
                        Te enviaremos un código de 6 dígitos por SMS para confirmar tu identidad.
                    </p>
                    <button
                        className="btn-primary btn-send-code"
                        onClick={sendVerificationCode}
                        disabled={verificationSending}
                    >
                        {verificationSending ? (
                            <>
                                <Loader2 className="icon-spin" /> Enviando...
                            </>
                        ) : (
                            <>
                                <Phone /> Enviar código SMS
                            </>
                        )}
                    </button>
                </div>
            ) : (
                <div className="verification-code-section">
                    <p className="verification-info">
                        Ingresa el código de 6 dígitos enviado a <strong>{clientPhone}</strong>
                    </p>

                    <div className="code-inputs">
                        {verificationCode.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => { codeInputRefs.current[index] = el; }}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                className="code-input"
                                autoFocus={index === 0}
                                disabled={verificationVerifying}
                            />
                        ))}
                    </div>

                    {verificationVerifying && (
                        <div className="verification-progress">
                            <Loader2 className="icon-spin" />
                            <span>Verificando...</span>
                        </div>
                    )}

                    <div className="resend-section">
                        {cooldown > 0 ? (
                            <p className="cooldown-text">
                                Reenviar código en <strong>{cooldown}s</strong>
                            </p>
                        ) : (
                            <button
                                className="btn-resend"
                                onClick={() => {
                                    setVerificationCode(['', '', '', '', '', '']);
                                    sendVerificationCode();
                                }}
                                disabled={verificationSending}
                            >
                                ¿No recibiste el código? <strong>Reenviar</strong>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {verificationError && (
                <div className="verification-error">
                    <p>{verificationError}</p>
                </div>
            )}

            <div className="step-actions">
                <button className="btn-secondary" onClick={goBack}>
                    <ChevronLeft /> Atrás
                </button>
            </div>
        </div>
    );

    // Render confirmation
    const renderConfirmation = () => {
        const isPendingApproval = bookingResult?.status === 'SCHEDULED' &&
            bookingResult?.confirmationMessage?.includes('solicitud');

        return (
            <div className="step-content confirmation-step">
                <div className="confirmation-icon">
                    <Sparkles className="sparkles" />
                    <Check className="check" />
                </div>
                <h2 className="confirmation-title">
                    {isPendingApproval ? '¡Solicitud Recibida!' : '¡Reserva Confirmada!'}
                </h2>
                <p className="confirmation-message">{bookingResult?.confirmationMessage}</p>

                {isPendingApproval && (
                    <div className="pending-approval-notice">
                        <Clock className="notice-icon" />
                        <div>
                            <strong>Pendiente de aprobación</strong>
                            <p>El salón revisará tu solicitud y te notificará la confirmación.</p>
                        </div>
                    </div>
                )}

                <div className="confirmation-details">
                    <div className="detail-row">
                        <span className="label">Servicio:</span>
                        <span className="value">{bookingResult?.serviceName}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Profesional:</span>
                        <span className="value">{bookingResult?.employeeName}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Fecha:</span>
                        <span className="value">
                            {bookingResult && new Date(bookingResult.startTime).toLocaleDateString('es-CO', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Hora:</span>
                        <span className="value">
                            {bookingResult && new Date(bookingResult.startTime).toLocaleTimeString('es-CO', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    </div>
                </div>

                <p className="whatsapp-notice">
                    📱 Te enviaremos un recordatorio por WhatsApp antes de tu cita.
                </p>
            </div>
        );
    };

    if (bookingConfig?.vipModeEnabled && !isVipVerified) {
        return (
            <div className="public-booking-page dark">
                <header className="booking-header">
                    <div className="salon-info">
                        <h1>{salon?.name}</h1>
                    </div>
                </header>
                <main className="booking-main">
                    <div className="step-content verification-step" style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
                        <div className="verification-shield">
                            <Shield />
                        </div>
                        <h2 className="step-title">Acceso Exclusivo</h2>
                        <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Este salón requiere un código de invitación para reservar.</p>
                        <form onSubmit={handleVipSubmit} className="client-form">
                            <div className="form-group">
                                <input
                                    type="text"
                                    value={vipCode}
                                    onChange={(e) => setVipCode(e.target.value)}
                                    placeholder="Ingresa tu código"
                                    style={{ textAlign: 'center', letterSpacing: '0.1em', color: '#f1f5f9' }}
                                    disabled={vipSubmitting}
                                />
                            </div>
                            {vipError && <p className="form-error" style={{ color: '#ef4444', marginTop: '0.5rem' }}>{vipError}</p>}
                            <button type="submit" className="btn-primary" disabled={!vipCode || vipSubmitting} style={{ marginTop: '1rem', width: '100%' }}>
                                {vipSubmitting ? <><Loader2 className="icon-spin" /> Verificando...</> : 'Desbloquear Reserva'}
                            </button>
                        </form>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className={`public-booking-page dark${isEmbedMode ? ' public-booking-page--embed' : ''}`}>
            {!isEmbedMode && (
                <header className="booking-header">
                    <div className="salon-info">
                        <h1>{salon?.name}</h1>
                        {salon?.address && (
                            <p className="salon-address">
                                <MapPin className="icon-small" />
                                {salon.address}
                            </p>
                        )}
                    </div>
                </header>
            )}

            {currentStep !== 'confirmation' && renderStepIndicator()}

            <main className="booking-main">
                {currentStep === 'service' && renderServiceStep()}
                {currentStep === 'employee' && renderEmployeeStep()}
                {currentStep === 'datetime' && renderDateTimeStep()}
                {currentStep === 'details' && renderDetailsStep()}
                {currentStep === 'verification' && renderVerificationStep()}
                {currentStep === 'confirmation' && renderConfirmation()}
            </main>

            {!isEmbedMode && (
                <footer className="booking-footer">
                    <p>Powered by <strong>Cresta</strong></p>
                </footer>
            )}
        </div>
    );
}
